import { DEFAULT_CATEGORY_TAXONOMY_CONFIG, isCategoryTaxonomyConfig, validateCategoryTaxonomy, type CategoryTaxonomyConfig } from "../category-taxonomy";
import type { FormConfigVersion } from "../form-config";

export const ADMIN_FORM_CONFIG_DRAFT_STORAGE_KEY = "meet-the-world:admin-form-config-draft:v1";

type PublicFormConfigResponse = {
  published?: {
    config: FormConfigVersion;
    updatedAt: string;
  } | null;
};

type PublicCategoryTaxonomyResponse = {
  published?: {
    config: CategoryTaxonomyConfig;
    updatedAt: string;
  } | null;
  useDefault?: boolean;
};

export type CategoryTaxonomyLoadStatus = "loading" | "ready" | "error";

export function parseStoredFormConfig(value: string | null): FormConfigVersion | null {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as FormConfigVersion;
    if (!parsed?.versionId || !Array.isArray(parsed.rules)) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function shouldUseLocalDraftFormConfigPreview(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("formConfigDraftPreview") === "1";
}

export function loadAdminDraftFormConfig(): FormConfigVersion | null {
  if (typeof window === "undefined" || !shouldUseLocalDraftFormConfigPreview()) return null;
  return parseStoredFormConfig(localStorage.getItem(ADMIN_FORM_CONFIG_DRAFT_STORAGE_KEY));
}

export async function fetchPublishedFormConfig(): Promise<FormConfigVersion | null> {
  const response = await fetch("/api/form-config", { credentials: "include" });
  if (!response.ok) return null;
  const body = (await response.json().catch(() => null)) as PublicFormConfigResponse | null;
  return body?.published?.config ?? null;
}

export async function fetchPublishedCategoryTaxonomy(): Promise<CategoryTaxonomyConfig> {
  const response = await fetch("/api/category-taxonomy", { credentials: "include" });
  if (!response.ok) throw new Error("Failed to load category taxonomy");
  const body = (await response.json().catch(() => null)) as PublicCategoryTaxonomyResponse | null;
  const config = body?.published?.config;
  if (isCategoryTaxonomyConfig(config)) {
    const diagnostics = validateCategoryTaxonomy(config);
    if (!diagnostics.some((diagnostic) => diagnostic.level === "error" || diagnostic.code === "missing_default_category")) return config;
  }
  if (body?.useDefault === true) return DEFAULT_CATEGORY_TAXONOMY_CONFIG;
  throw new Error("Published category taxonomy is missing or invalid");
}
